/**
 * usePushNotifications hook
 * 
 * Handles Web Push subscription lifecycle:
 * - Requests notification permission
 * - Subscribes to push via service worker
 * - Sends subscription to backend
 * - Provides notification history for the in-app bell
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

export function usePushNotifications(isAuthenticated) {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [subscribed, setSubscribed] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (permission !== 'granted') return;

    try {
      // Get VAPID public key from backend
      const { data: vapidData } = await axios.get(`${API}/push-notifications/vapid-key`);
      if (!vapidData.public_key) return;

      const registration = await navigator.serviceWorker.ready;

      // Check existing subscription
      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        const key = urlBase64ToUint8Array(vapidData.public_key);
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: key,
        });
      }

      // Send subscription to backend
      const subJson = subscription.toJSON();
      await axios.post(`${API}/push-notifications/subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      });

      setSubscribed(true);
    } catch (err) {
      console.error('Push subscription failed:', err);
    }
  }, [permission]);

  // Request permission and subscribe
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) return false;
    const result = await Notification.requestPermission();
    setPermission(result);
    if (result === 'granted') {
      await subscribe();
    }
    return result === 'granted';
  }, [subscribe]);

  // Auto-subscribe when authenticated and permission is granted
  useEffect(() => {
    if (isAuthenticated && permission === 'granted') {
      subscribe();
    }
  }, [isAuthenticated, permission, subscribe]);

  // Fetch notification history
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/push-notifications/history?limit=30`);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
      // Keep the home-screen icon badge in step with the in-app count
      // (installed PWA via the Badging API; native wrapper via the bridge).
      try {
        const n = Number(data.unread_count || 0);
        if (typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
          if (n > 0) navigator.setAppBadge(n); else navigator.clearAppBadge();
        }
        window.NativeApp?.setBadge?.(n);
      } catch { /* platform noop */ }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Poll for new notifications
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchNotifications]);

  // Mark all as read
  const markAllRead = useCallback(async () => {
    try {
      await axios.post(`${API}/push-notifications/mark-read?mark_all=true`);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {
      // silent
    }
  }, []);

  // Send test notification
  const sendTest = useCallback(async () => {
    try {
      await axios.post(`${API}/push-notifications/test`);
      setTimeout(fetchNotifications, 1000);
      return true;
    } catch {
      return false;
    }
  }, [fetchNotifications]);

  return {
    permission,
    subscribed,
    notifications,
    unreadCount,
    loading,
    requestPermission,
    markAllRead,
    sendTest,
    refetch: fetchNotifications,
  };
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
